import { Box, Divider, Link, Text } from '@factorialco/deprecated-design-system'
import { DetailsItem, DetailsItemsList } from '@factorialco/f0-react/dist/experimental'

import { observer } from 'mobx-react'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { MoneyWithStringCurrency } from 'modules/project_management/lib/formatText'
import { IProjectSpendingPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SpendingPage/types'

export interface IProjectSpendingDetailSidepanel {
  onClose: () => void
  projectSpendingPanelData: IProjectSpendingPanelData
  showSpendingDetailPanel: boolean
}

const ProjectSpendingDetailSidepanel = ({
  onClose,
  projectSpendingPanelData,
  showSpendingDetailPanel,
}: IProjectSpendingDetailSidepanel) => {
  const { navigation } = useNavigation()
  const {
    effectiveOn,
    employeeName,
    exchangeRate,
    legalEntityLegalName,
    legalEntityAmountCents,
    legalEntityAmountCurrency,
    originalAmountCents,
    originalAmountCurrency,
  } = projectSpendingPanelData

  const {
    access: { locale: accessLocale },
  } = useCurrentSession()

  const differentCurrency = originalAmountCurrency !== legalEntityAmountCurrency
  const formattedExchangeRate = (+(exchangeRate || 1).toFixed(2) * 100) | 0

  const formatedRate =
    legalEntityAmountCurrency &&
    MoneyWithStringCurrency(formattedExchangeRate, legalEntityAmountCurrency, accessLocale)

  const isExpensable = 'expensableId' in projectSpendingPanelData

  return (
    <Sidepanel
      title={i18n.t('project_management.spending.spending_tab')}
      open={showSpendingDetailPanel}
      width="wide"
      onClose={onClose}
    >
      <Box gap="s24">
        <Box gap="s16">
          <DetailsItem
            title={i18n.t('common.employee')}
            content={[
              {
                type: 'item',
                text: employeeName,
              },
            ]}
          />
        </Box>
        <Box gap="s16">
          <DetailsItem
            title={i18n.t('common.legal_entity')}
            content={[
              {
                type: 'item',
                text: legalEntityLegalName || '',
              },
            ]}
          />
        </Box>

        <Box gap="s8" paddingLeft="s8">
          {/* TODO: Adapt format when DetailsItem is ready */}
          <Text size="200">{i18n.t('project_management.spending.table.side_panel.source')}</Text>
          {isExpensable ? (
            <Link.Router
              to={navigation.expenses.list.expenses.expenseId.fullPath({
                expense_id: projectSpendingPanelData.expensableId,
              })}
              external
            >
              {i18n.t('project_management.spending.table.side_panel.expense_document')}
            </Link.Router>
          ) : (
            <Link.Router
              to={navigation.finance.invoices.list.invoiceId.fullPath({
                invoiceId: projectSpendingPanelData.financialDocumentId,
              })}
              external
            >
              {i18n.t('project_management.spending.table.side_panel.invoice_document')}
            </Link.Router>
          )}
        </Box>

        {!!legalEntityAmountCents && legalEntityAmountCurrency && differentCurrency && (
          <Box gap="s24" paddingLeft="s8">
            <Divider />
            <Box gap="s16">
              <Text color="grey1000" size="300" weight="bold">
                {i18n.t('project_management.spending.table.side_panel.currency_conversion')}
              </Text>
              <Text.Normal>
                {i18n.t('project_management.spending.table.side_panel.conversion_explanation')}
              </Text.Normal>
            </Box>

            <DetailsItemsList
              tableView
              details={[
                {
                  title: i18n.t('project_management.spending.table.side_panel.original_amount'),
                  content: {
                    type: 'item',
                    text: MoneyWithStringCurrency(
                      originalAmountCents,
                      originalAmountCurrency,
                      accessLocale
                    ),
                  },
                },
                {
                  title: i18n.t('project_management.spending.table.side_panel.converted_amount'),
                  content: {
                    type: 'item',
                    text: MoneyWithStringCurrency(
                      legalEntityAmountCents,
                      legalEntityAmountCurrency,
                      accessLocale
                    ),
                  },
                },
                {
                  title: i18n.t('common.rate'),
                  content: {
                    type: 'item',
                    text: `${MoneyWithStringCurrency(
                      100,
                      originalAmountCurrency,
                      accessLocale
                    )} = ${formatedRate}`,
                  },
                },
                {
                  title: i18n.t('common.date_date'),
                  content: {
                    type: 'item',
                    text: dateBackendDateTimeToUI(effectiveOn),
                  },
                },
              ]}
            />
          </Box>
        )}
      </Box>
    </Sidepanel>
  )
}

export default observer(ProjectSpendingDetailSidepanel)
